// frontend/src/components/Auth/SupabaseAuth.tsx
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { typedApi } from '../../services/typedApiClient';
import {
  GraduationCap,
  Loader2,
  Wand2,
  ListChecks,
  MessageCircle,
  Sparkles,
} from 'lucide-react';
import { Badge, BrandMark, Button, Card } from '../ui';

import LoginModal from './LoginModal';

export interface SupabaseAuthProps {
  children?: ReactNode;
}

interface OpenDomain {
  domain: string;
  institution: string;
}

/** How long the Get-started area waits for the open schools before it shows
 *  neutral copy. Long enough for a normal read, short enough that a slow API
 *  never hides sign-in. */
export const OPEN_DOMAINS_WAIT_MS = 1500;

interface OpenDomainsState {
  /** The open schools' domains; `null` until the read succeeds, and after a
   *  failed read, when the page shows neutral copy since neither
   *  "invite-only" nor "free for" can be known to be true. */
  domains: OpenDomain[] | null;
  /** The read is still in flight and the wait has not run out: the page
   *  holds the Get-started area rather than render a box that a school box
   *  would then push down. */
  pending: boolean;
}

/** The schools with open access, for the sign-in copy (open-access plan §3.9).
 *  Read from the backend so the copy follows the launch switch: empty before
 *  launch, UMD alone after the UMD flip. */
function useOpenDomains(enabled: boolean): OpenDomainsState {
  const [domains, setDomains] = useState<OpenDomain[] | null>(null);
  const [settled, setSettled] = useState(false);
  const [waitedOut, setWaitedOut] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!cancelled) setWaitedOut(true);
    }, OPEN_DOMAINS_WAIT_MS);
    typedApi.access
      .openDomains()
      .then((data) => {
        if (!cancelled) setDomains((data?.domains ?? []) as OpenDomain[]);
      })
      .catch(() => {
        // leave null: neutral copy
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled]);
  return { domains, pending: enabled && !settled && !waitedOut };
}

function joinWords(words: string[], conjunction: string): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} ${conjunction} ${words[words.length - 1]}`;
}

interface OpenSchool {
  institution: string;
  addresses: string[];
}

/** One entry per open school, its domains as "@domain", in the order the
 *  backend lists them (sorted by domain). */
export function openSchools(domains: OpenDomain[] | null): OpenSchool[] {
  const schools: OpenSchool[] = [];
  for (const d of domains ?? []) {
    let school = schools.find((s) => s.institution === d.institution);
    if (!school) {
      school = { institution: d.institution, addresses: [] };
      schools.push(school);
    }
    school.addresses.push(`@${d.domain}`);
  }
  return schools;
}

/** The general sign-in box's line: for students who already have an account
 *  or an invitation once a school is open, the invite-only line before
 *  launch, or neutral copy while the open schools are unknown. The free
 *  offer itself has its own box per school (`OpenSchoolCard`). */
export function accessCopy(domains: OpenDomain[] | null): string {
  if (domains === null) {
    return 'Sign in with Google or your email.';
  }
  if (domains.length === 0) {
    return 'My Academic Planner is invite-only for now. Sign in with Google or your email, '
      + 'using the address your invitation was sent to.';
  }
  return 'Already have an account, or an invitation? Sign in with Google or your email.';
}

const SupabaseAuth = ({ children }: SupabaseAuthProps = {}) => {
  const { user, loading: authLoading } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { domains: openDomains, pending: openDomainsPending } = useOpenDomains(!authLoading && !user);
  const schools = openSchools(openDomains);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-surface-ground flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-ink-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <>{children}</>;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-surface-ground text-ink">
      {/* Warm brand glow behind the hero — depth without a generic blue wash. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -top-48 h-[28rem] bg-gradient-to-b from-primary/10 via-accent/5 to-transparent blur-2xl"
      />

      <header className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* enosys teal, not an institution color: nobody is signed in
                  yet, so no school is resolved — here the mark signs the
                  company's name. Decorative, because the wordmark beside it
                  already names the product. */}
              <BrandMark size={36} variant="enosys" />
              <span className="text-lg font-bold tracking-tight text-ink">My Academic Planner</span>
            </div>
            <Button intent="secondary" size="sm" onClick={() => setShowLoginModal(true)}>
              Sign in
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
          {/* Hero */}
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-line-soft bg-surface-card px-3 py-1 text-xs font-medium text-ink-2 shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-accent-600" />
              AI-guided degree planning
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-ink">
              Plan your path to{' '}
              <span className="relative whitespace-nowrap text-primary">
                graduation
                <span
                  aria-hidden="true"
                  className="absolute -bottom-1 left-0 h-1 w-full rounded-full bg-accent/60"
                />
              </span>
            </h1>
            <p className="mt-5 text-lg text-ink-2 leading-relaxed">
              Generate a complete path to graduation, see every requirement you've met, and get
              AI guidance the whole way.
            </p>
          </div>

          {/* Get started: a box per open school, then the general sign-in */}
          {openDomainsPending ? (
            <div className="mt-10 max-w-md mx-auto" aria-busy="true">
              {/* The height of the Get-started box, so nothing jumps when it renders. */}
              <Card className="h-[10.5rem] p-6" data-testid="get-started-pending">
                <span className="sr-only">Loading sign-in options…</span>
              </Card>
            </div>
          ) : (
            <div className="mt-10 max-w-md mx-auto space-y-4 animate-scale-in">
              {schools.map((school) => (
                <OpenSchoolCard
                  key={school.institution}
                  school={school}
                  onSignIn={() => setShowLoginModal(true)}
                />
              ))}
              <Card className="p-6 text-center">
                <h2 className="text-base font-semibold text-ink">
                  {schools.length > 0 ? 'Everyone else' : 'Get started'}
                </h2>
                <p className="mt-1 text-sm text-ink-2">{accessCopy(openDomains)}</p>
                <Button
                  className="mt-4 w-full"
                  intent={schools.length > 0 ? 'secondary' : 'primary'}
                  onClick={() => setShowLoginModal(true)}
                >
                  {schools.length > 0 ? 'Sign in' : 'Sign in to get started'}
                </Button>
              </Card>
            </div>
          )}

          {/* Features */}
          <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-3 max-w-4xl mx-auto">
            <FeatureCard
              icon={<Wand2 className="h-6 w-6" />}
              tone="primary"
              title="Instant plans"
              description="Generate a complete, prerequisite-valid path to graduation in seconds — then adjust it your way."
            />
            <FeatureCard
              icon={<ListChecks className="h-6 w-6" />}
              tone="accent"
              title="Live degree audit"
              description="Always know exactly what you've satisfied and what's left — across majors, minors, and gen-eds, with prerequisites and credit caps checked as you go."
            />
            <FeatureCard
              icon={<MessageCircle className="h-6 w-6" />}
              tone="primary"
              title="AI advisor"
              description="Ask anything and get personalized course picks and answers grounded in your real plan."
            />
          </div>
        </div>

        <footer className="relative z-10 border-t border-line-soft">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-1 text-center text-sm">
            <p className="text-ink-2">
              Built by{' '}
              {/* hover uses the enosys brand teal (their credit, not the institution
                  primary) via --color-enosys, the same token the brand mark's enosys
                  variant reads — it was a separate hardcoded hex until this change. */}
              <a
                href="https://enosys.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-ink transition-colors duration-fast hover:text-[rgb(var(--color-enosys))]"
              >
                enosys.ai
              </a>
            </p>
            <p className="text-ink-3">
              An independent academic planning tool. Not affiliated with any university.
            </p>
            <p className="text-ink-3">
              <Link to="/privacy" className="hover:text-ink transition-colors duration-fast underline">
                Privacy Policy
              </Link>
              <span className="mx-2" aria-hidden="true">·</span>
              <Link to="/terms" className="hover:text-ink transition-colors duration-fast underline">
                Terms of Service
              </Link>
            </p>
          </div>
        </footer>
      </main>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </div>
  );
};

interface OpenSchoolCardProps {
  school: OpenSchool;
  onSignIn: () => void;
}

/** The free offer for one open school, in its own box so it is the first
 *  thing an eligible student reads, not a line inside the general sign-in. */
const OpenSchoolCard = ({ school, onSignIn }: OpenSchoolCardProps) => {
  const addresses = joinWords(school.addresses, 'or');
  return (
    <Card
      className="p-6 text-center border-2 border-primary/40 ring-4 ring-primary/10"
      role="region"
      aria-label={`${school.institution} students`}
    >
      <div className="flex items-center justify-center gap-2">
        <GraduationCap className="h-5 w-5 text-primary" aria-hidden="true" />
        <h2 className="text-base font-semibold text-ink">{school.institution} students</h2>
        <Badge status="completed">Free</Badge>
      </div>
      <p className="mt-2 text-sm text-ink-2">
        No invitation needed. Sign in with your{' '}
        <span className="font-semibold text-ink">{addresses}</span> email.
      </p>
      <Button className="mt-4 w-full" onClick={onSignIn}>
        Sign in free with {addresses}
      </Button>
    </Card>
  );
};

type FeatureTone = 'primary' | 'accent';

interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  tone: FeatureTone;
}

const FeatureCard = ({ icon, title, description, tone }: FeatureCardProps) => {
  const chip =
    tone === 'accent'
      ? 'bg-accent-500/20 text-accent-700'
      : 'bg-primary/10 text-primary';

  return (
    <Card interactive className="p-5">
      <div className={`inline-flex items-center justify-center rounded-control p-3 ${chip}`}>
        {icon}
      </div>
      <h3 className="mt-4 text-base font-semibold text-ink">{title}</h3>
      <p className="mt-2 text-sm text-ink-2 leading-relaxed">{description}</p>
    </Card>
  );
};

export default SupabaseAuth;
